import React from 'react';
import { Card } from '@/components/ui/card';
import { Flower2, Heart, Sparkles } from 'lucide-react';

export const About = () => {
    return (
        <div className="min-h-screen pt-32 pb-20 px-6 sm:px-8 lg:px-12">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="text-center mb-16 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-20 h-20 mb-8 rounded-full bg-primary/10 animate-breathe">
                        <Flower2 className="w-10 h-10 text-primary" />
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-light text-foreground mb-6">
                        About Focus Bloom
                    </h1>
                </div>
                
                {/* Main Content */}
                <Card className="p-8 sm:p-12 bg-card border-border mb-12 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                    <div className="prose prose-lg max-w-none">
                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                            Focus Bloom was created by <span className="text-foreground font-medium">Deodath</span> with a simple yet profound mission: to help people enhance their focus and find peace through visual mindfulness.
                        </p>
                        
                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                            In our modern world filled with constant distractions and endless notifications, the ability to maintain focus has become a rare and precious skill. Focus Bloom offers a sanctuary—a digital space designed to help you reconnect with your inner calm and strengthen your concentration.
                        </p>
                        
                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                            Inspired by the timeless beauty of nature and the principles of mindful observation, each exercise on this platform is carefully crafted to guide your attention gently and naturally. Like watching a flower bloom in a quiet garden, these practices invite you to slow down, breathe, and simply be present.
                        </p>
                    </div>
                </Card>
                
                {/* Benefits Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                    <Card className="p-6 bg-card border-border animate-fade-in" style={{ animationDelay: '0.3s' }}>
                        <div className="flex items-start gap-4">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 flex-shrink-0">
                                <Heart className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                                <h3 className="text-lg font-serif font-medium text-foreground mb-2">
                                    Reduced Stress
                                </h3>
                                <p className="text-muted-foreground leading-relaxed">
                                    Regular practice helps lower anxiety and promotes a sense of calm and well-being.
                                </p>
                            </div>
                        </div>
                    </Card>
                    
                    <Card className="p-6 bg-card border-border animate-fade-in" style={{ animationDelay: '0.4s' }}>
                        <div className="flex items-start gap-4">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-secondary/10 flex-shrink-0">
                                <Sparkles className="w-6 h-6 text-secondary" />
                            </div>
                            <div>
                                <h3 className="text-lg font-serif font-medium text-foreground mb-2">
                                    Improved Attention Span
                                </h3>
                                <p className="text-muted-foreground leading-relaxed">
                                    Train your mind to stay focused for longer periods, improving productivity and clarity.
                                </p>
                            </div>
                        </div>
                    </Card>
                </div>
                
                {/* Philosophy Section */}
                <Card className="p-8 sm:p-12 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 border-border animate-fade-in" style={{ animationDelay: '0.5s' }}>
                    <h2 className="text-2xl font-serif font-medium text-foreground mb-6 text-center">
                        Our Philosophy
                    </h2>
                    <div className="space-y-4 text-muted-foreground leading-relaxed">
                        <p>
                            <span className="text-foreground font-medium">Simplicity:</span> We believe that the most powerful tools are often the simplest. No complex techniques, no overwhelming features—just pure, focused practice.
                        </p>
                        <p>
                            <span className="text-foreground font-medium">Nature-Inspired:</span> Our exercises draw from the natural world, using imagery and rhythms that resonate with our innate sense of peace and wonder.
                        </p>
                        <p>
                            <span className="text-foreground font-medium">Accessibility:</span> Mindfulness should be available to everyone, everywhere. That's why Focus Bloom is free, with no sign-ups or subscriptions required.
                        </p>
                        <p>
                            <span className="text-foreground font-medium">Distraction-Free:</span> In a world of ads and pop-ups, we offer a clean, serene space where your only focus is your practice.
                        </p>
                    </div>
                </Card>
            </div>
        </div>
    );
};